<template>
  <div class="p-field">
    <h4>{{ linesConfig.label || 'Linhas do Dossier' }}</h4>

    <div v-if="Object.keys(aggregatedStocks).length > 0" class="stocks-info-container p-mb-3">
      <h5 class="p-d-flex p-ai-center p-m-0 p-mb-2">
        <i class="pi pi-info-circle p-mr-2 text-info" style="font-size: 1.15rem;"></i>
        <span>Stock Disponível nos Armazéns</span>
      </h5>
      <div class="stocks-grid">
        <div
          v-for="(warehousesInfo, prodRef) in aggregatedStocks"
          :key="prodRef"
          class="stock-card"
        >
          <div class="stock-ref">{{ prodRef }}</div>
          <div class="stock-warehouses-list">
            <span
              v-for="(qty, whLabel) in warehousesInfo"
              :key="whLabel"
              class="stock-badge"
            >
              {{ whLabel }}: <b>{{ qty }}</b>
            </span>
          </div>
        </div>
      </div>
    </div>

    <DataTable
      :key="`dossier-table-${typeDoc}`"
      :value="localLines"
      responsiveLayout="scroll"
      :paginator="false"
      class="p-datatable-sm dossier-lines-table"
    >
      <Column
        v-for="(col, colIdx) in tableColumns"
        :key="`col-${typeDoc}-${col.key}-${colIdx}`"
        :field="col.key"
        :header="col.label"
        :headerStyle="getColumnWidth(col.key)"
        :bodyStyle="getColumnWidth(col.key)"
      >
        <template #body="slotProps">
          <template v-if="canEdit">
            <!-- Referência Autocomplete -->
            <div v-if="col.type === 'autocomplete' && col.key === 'product_ref'" style="display: flex; align-items: center; width: 100%;">
              <div style="flex: 1;">
                <AutoComplete
                  :key="`autocomplete-product-${slotProps.index}`"
                  v-model="slotProps.data[col.key]"
                  @complete="searchProduct"
                  :suggestions="productSuggestions"
                  field="code"
                  placeholder="Pesquise produto"
                  @item-select="(e) => onProductSelect(e.value, slotProps.index, slotProps.data.id)"
                  style="width: 100%"
                  :dropdown="true"
                  appendTo="body"
                >
                  <template #item="itemSlot">
                    <div>
                      <strong>{{ itemSlot.item.code }}</strong> - {{ itemSlot.item.description }}
                    </div>
                  </template>
                </AutoComplete>
              </div>
              <Button
                icon="pi pi-plus"
                class="p-button-primary p-button-outlined p-ml-1"
                style="padding: 0; width: 33px; height: 33px; min-width: 33px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;"
                v-tooltip.top="'Criar Novo Produto'"
                @click="openAddProductDialog(slotProps.data, slotProps.index)"
              />
            </div>

            <!-- Warehouse fields -->
            <span v-else-if="isWarehouseField(col.key) && col.type === 'autocomplete'">
              <div class="p-mb-1 warehouse-option-stock" v-if="slotProps.data[col.key] && lineStocks[slotProps.data.id]">
                  Stock: {{ lineStocks[slotProps.data.id][slotProps.data[col.key]] || 0 }}
              </div>
              <Dropdown
                dataKey="reference"
                v-model="slotProps.data[col.key]"
                :key="`autocomplete-warehouse-${slotProps.index}`"
                :options="listWarehouses"
                optionLabel="name"
                optionValue="reference"
                @change="(e) => onWarehouseSelect(e.value, slotProps.index, col.key)"
                filter
                filterBy="reference,name"
                placeholder="Selecionar armazém"
                :virtualScrollerOptions="{ itemSize: 38 }"
                appendTo="body"
                :style="{ width: '100%' }"
              >
                <template #option="slotOptions">
                  <div class="p-d-flex p-jc-between p-ai-center">
                    <div>
                      <strong>{{ slotOptions.option.reference }}</strong> -
                      {{ slotOptions.option.name }}
                    </div>
                    <div v-if="lineStocks[slotProps.data.id]" class="warehouse-option-stock">
                      <span>
                        Stock: {{ lineStocks[slotProps.data.id][slotOptions.option.reference] || 0 }}
                      </span>
                    </div>
                  </div>
                </template>
              </Dropdown>
            </span>

            <!-- Warehouse Destination fields -->
            <Dropdown
              v-else-if="isWarehouseDestinationField(col.key) && col.type === 'autocomplete'"
              dataKey="reference"
              v-model="slotProps.data[col.key]"
              :key="`autocomplete-warehouse-destination-${slotProps.index}`"
              :options="listWarehousesDestination"
              optionLabel="name"
              optionValue="reference"
              @change="(e) => onWarehouseSelect(e.value, slotProps.index, col.key)"
              filter
              filterBy="reference,name"
              placeholder="Selecionar armazém destino"
              :virtualScrollerOptions="{ itemSize: 38 }"
              appendTo="body"
              :style="{ width: '100%' }"
            >
              <template #option="slotProps">
                <div class="p-d-flex p-jc-between p-ai-center">
                  <div>
                    <strong>{{ slotProps.option.reference }}</strong> -
                    {{ slotProps.option.name }}
                  </div>
                </div>
              </template>
            </Dropdown>

            <!-- Text fields -->
            <InputText
              v-else-if="col.type === 'text'"
              :key="`text-${slotProps.index}`"
              v-model="slotProps.data[col.key]"
              :placeholder="col.label"
              style="width: 100%"
            />

            <!-- Number fields -->
            <InputNumber
              v-else-if="col.type === 'number'"
              :key="`number-${slotProps.index}`"
              v-model="slotProps.data[col.key]"
              :min="col.config.min || 0"
              :minFractionDigits="0"
              :maxFractionDigits="col.config.decimals || 6"
              style="width: 100%"
              @input="calculateLine(slotProps.index)"
            />

            <!-- Currency fields -->
            <InputNumber
              v-else-if="col.type === 'currency'"
              :key="`currency-${slotProps.index}`"
              v-model="slotProps.data[col.key]"
              mode="currency"
              :currency="col.config.currency || 'EUR'"
              locale="pt-PT"
              :minFractionDigits="2"
              :maxFractionDigits="col.config.decimals || 6"
              style="width: 100%"
              @input="calculateLine(slotProps.index)"
            />

            <!-- Vat select fields -->
            <Dropdown
              v-else-if="col.type === 'vat_select'"
              :key="`dropdown-${slotProps.index}`"
              v-model="slotProps.data[col.key]"
              :options="listVats"
              optionLabel="value"
              optionValue="code"
              dataKey="code"
              placeholder="IVA"
              style="width: 100%"
              appendTo="body"
              @change="calculateLine(slotProps.index)"
            />

            <!-- Total display -->
            <span v-else-if="col.type === 'total_display'" class="p-text-bold">
              {{ formatCurrency(slotProps.data.total) }}
            </span>

            <!-- Delete action -->
            <Button
              v-else-if="col.type === 'delete_action'"
              icon="pi pi-trash"
              class="p-button-rounded p-button-danger p-button-text"
              @click="removeLine(slotProps.index)"
            />

            <!-- Fallback display -->
            <span v-else>{{ slotProps.data[col.key] }}</span>
          </template>

          <template v-else>
            <span v-if="col.type === 'total_display'" class="p-text-bold">
              {{ formatCurrency(slotProps.data.total) }}
            </span>
            <span v-else-if="col.type === 'currency'">
              {{ formatCurrency(slotProps.data[col.key]) }}
            </span>
            <span v-else-if="col.type === 'delete_action'"></span>
            <span v-else>{{ slotProps.data[col.key] }}</span>
          </template>
        </template>
      </Column>
    </DataTable>

    <Button
      v-if="canEdit"
      label="Adicionar Linha"
      icon="pi pi-plus"
      class="p-button-sm p-mt-2"
      @click="addLine"
    />

    <Dialog
      header="Criar Novo Produto"
      :visible.sync="showAddProductDialog"
      :style="{ width: '450px' }"
      :modal="true"
      @hide="closeAddProductDialog"
    >
      <div class="p-fluid">
        <div class="p-field">
          <label for="new-prod-ref">Referência *</label>
          <InputText
            id="new-prod-ref"
            v-model="newProduct.ref"
            placeholder="Ex: PROD123"
            :class="{ 'p-invalid': newProductErrors.ref }"
          />
          <small class="p-error" v-if="newProductErrors.ref && newProductErrors.ref.length">{{ newProductErrors.ref[0] }}</small>
        </div>
        <div class="p-field">
          <label for="new-prod-design">Designação *</label>
          <InputText
            id="new-prod-design"
            v-model="newProduct.design"
            placeholder="Ex: Descrição do produto"
            :class="{ 'p-invalid': newProductErrors.design }"
          />
          <small class="p-error" v-if="newProductErrors.design && newProductErrors.design.length">{{ newProductErrors.design[0] }}</small>
        </div>
        <div class="p-field">
          <label for="new-prod-brand">Marca *</label>
          <InputText
            id="new-prod-brand"
            v-model="newProduct.brand"
            placeholder="Ex: Marca do produto"
            :class="{ 'p-invalid': newProductErrors.brand }"
          />
          <small class="p-error" v-if="newProductErrors.brand && newProductErrors.brand.length">{{ newProductErrors.brand[0] }}</small>
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancelar"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeAddProductDialog"
          :disabled="savingProduct"
        />
        <Button
          label="Guardar"
          icon="pi pi-check"
          class="p-button-success"
          @click="saveNewProduct"
          :loading="savingProduct"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import stockService from '@/services/stock.service';

export default {
  name: 'AppDynamicTableLines',

  props: {
    linesConfig: {
      type: Object,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    useVat: {
      type: Boolean,
      default: true
    },
    typeDoc: {
      default: 0
    },
    canEdit: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      localLines: [],
      productSuggestions: [],
      warehouseOptions: [],
      listWarehouses: [],
      listWarehousesDestination: [],
      listVats: [],
      lineStocks: {},
      previousRefs: {},
      showAddProductDialog: false,
      savingProduct: false,
      newProduct: {
        ref: '',
        design: '',
        brand: ''
      },
      newProductErrors: {},
      newProductLineIndex: null,
      newProductLineId: null,
      loading: false
    };
  },

  computed: {
    aggregatedStocks() {
      const result = {};

      this.localLines.forEach(line => {
        const ref = line.product_ref;
        if (!ref) return;

        const stocks = this.lineStocks[line.id];
        if (!stocks) return;

        const tempWarehouses = {};
        let hasStock = false;

        for (const [wh, qty] of Object.entries(stocks)) {
          const numQty = parseFloat(qty) || 0;
          if (numQty > 0) {
            const whInfo = this.listWarehouses.find(w => String(w.reference) === String(wh));
            const whLabel = whInfo ? `${wh} - ${whInfo.name}` : `Armazém ${wh}`;
            tempWarehouses[whLabel] = numQty;
            hasStock = true;
          }
        }

        if (hasStock) {
          if (!result[ref]) {
            result[ref] = {};
          }
          Object.assign(result[ref], tempWarehouses);
        }
      });

      return result;
    },
    tableColumns() {
      if (!this.linesConfig || !this.linesConfig.fields) return [];

      const fields = Object.entries(this.linesConfig.fields)
        .filter(([key]) => key !== 'total')
        .map(([key, config]) => ({
          key,
          type: config.type,
          label: config.label,
          config
        }));

      fields.push({
        key: 'total',
        type: 'total_display',
        label: 'Total',
        config: { label: 'Total' }
      });

      if (this.canEdit) {
        fields.push({
          key: 'delete_action',
          type: 'delete_action',
          label: '',
          config: { label: '' }
        });
      }

      return fields;
    }
  },

  watch: {
    value: {
      immediate: true,
      handler: async function(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.localLines)) {
          this.localLines = newVal && newVal.length > 0
            ? await Promise.all(newVal.map(line => this.normalizeLineValues(line)))
            : [];
        }
      }
    },
    localLines: {
      deep: true,
      handler(newLines) {
        newLines.forEach(line => {
          const prevRef = this.previousRefs[line.id];
          const currentRef = line.product_ref ? String(line.product_ref).trim() : '';

          if (currentRef !== prevRef) {
            this.$set(this.previousRefs, line.id, currentRef);
            if (!currentRef) {
              this.$delete(this.lineStocks, line.id);
            } else {
              this.verifyWarehouses(currentRef, line.id);
            }
          }
        });

        // Clean up previousRefs for deleted lines
        const currentIds = newLines.map(l => l.id);
        Object.keys(this.previousRefs).forEach(id => {
          if (!currentIds.includes(id)) {
            this.$delete(this.previousRefs, id);
          }
        });
      }
    }
  },

  async created() {
    this.getWarehouses();
    await this.getListVats();
    this.calculateAllLines();
  },

  methods: {
    async normalizeLineValues(line) {
      const normalized = { ...line };

      if (normalized.product_ref) {
        normalized.product_ref = String(normalized.product_ref).trim();
        this.$set(this.previousRefs, normalized.id, normalized.product_ref);
        await this.verifyWarehouses(normalized.product_ref, normalized.id);
      } else {
        this.$set(this.previousRefs, normalized.id, '');
      }

      const numericFields = ['quantity', 'price_cost', 'sale_price', 'total'];
      numericFields.forEach(field => {
        if (field in normalized && normalized[field] !== null) {
          normalized[field] = parseFloat(normalized[field]) || 0;
        }
      });

      return normalized;
    },
    async getListVats() {
      const response = await stockService.getListVats();
      this.listVats = response.data;
    },
    getColumnWidth(fieldKey) {
      const widths = {
        product_ref: '280px',
        description: '280px',
        quantity: '90px',
        price_cost: '10%',
        sale_price: '15%',
        discount: '5%',
        warehouse_origin: '10%',
        warehouse_destination: '10%',
        warehouse: '10%',
        supplier_ref: '10%',
        total: '15%',
        delete_action: '5%'
      };
      return {
        'width': widths[fieldKey] || 'auto',
        'min-width': widths[fieldKey] || 'auto'
      };
    },

    isWarehouseField(fieldKey) {
      return ['warehouse', 'warehouse_origin'].includes(fieldKey);
    },

    isWarehouseDestinationField(fieldKey) {
      return ['warehouse_destination'].includes(fieldKey);
    },

    getWarehouses() {
      stockService.getStorages().then((response) => {
        this.listWarehouses = response.data.map((item) => {
          return {
            reference: String(item.no),
            name: item.nome,
            stock: 0
          };
        });

        this.listWarehousesDestination = response.data.map((item) => {
          return {
            reference: String(item.no),
            name: item.nome,
            stock: 0
          };
        });
      });
    },

    async verifyWarehouses(code, id) {
      if (!code) return;

      this.loading = true;
      try {
        const response = await stockService.getWarehousesByReference(code);

        if (response && response.length === 0) {
          return;
        }

        const stocks = {};
        for (const line of response) {
          if (line.ref === code) {
            stocks[line.warehouse] = line.stock;
          }
        }
        this.$set(this.lineStocks, id, stocks);

      } catch (err) {
        // todo
      } finally {
        this.loading = false;
      }
    },

    async searchProduct(event) {
      try {
        const query = event.query;
        const response = await stockService.getQueryProducts({ search: query });
        this.productSuggestions = response.data.data;
      } catch (error) {
        console.error('Erro ao buscar produtos:', error);
        this.productSuggestions = [];
      }
    },

    async onProductSelect(product, index, id) {
      if (this.localLines.map(e => e.product_ref).includes(product.code)) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Aviso',
          detail: `Produto já adicionado nas linhas (${product.code})`,
          life: 3000
        });
        this.localLines[index].product_ref = null;
        return;
      }
      this.localLines[index].product_ref = product.code;
      this.localLines[index].description = product.description || '';

      if ('price_cost' in this.linesConfig.fields) {
        this.localLines[index].price_cost = product.price_cost || 0;
      }

      if ('sale_price' in this.linesConfig.fields) {
        this.localLines[index].sale_price = product.sale_price || 0;
      }

      if ('vat' in this.linesConfig.fields) {
        this.localLines[index].vat = product.vat || 2;
      }

      const stocks = {};
      product.stock_warehouses.forEach(sw => {
        stocks[sw.warehouse] = sw.quantity;
      });

      this.$set(this.lineStocks, id, stocks);

      this.validateStock(index, id);

      this.localLines[index].quantity = 1;
      this.calculateLine(index);
      this.$emit('input', this.localLines);
    },

    onWarehouseSelect(warehouse, index, fieldKey) {
      this.localLines[index][fieldKey] = warehouse;
      if (fieldKey === 'warehouse_origin') {
        this.validateStock(index, this.localLines[index].id);
      }
      this.$emit('input', this.localLines);
    },

    validateStock(index, id) {
      const line = this.localLines[index];
      const originWh = line.warehouse_origin;
      const qty = line.quantity;

      if (!originWh || !this.lineStocks[id]) return true;

      const available = this.lineStocks[id][originWh] || 0;

      if (qty > available) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Stock Insuficiente',
          detail: `O armazém ${originWh} tem ${available} unidades.`,
          life: 5000
        });
        // line.quantity = available;
        return false;
      }
      return true;
    },

    addLine() {
      const newLine = {};

      newLine.id = String(Math.random().toString(36).slice(2));

      Object.keys(this.linesConfig.fields).forEach(fieldKey => {
        const fieldConfig = this.linesConfig.fields[fieldKey];

        if (fieldConfig.type === 'number' || fieldConfig.type === 'currency') {
          newLine[fieldKey] = 0;
        } else {
          newLine[fieldKey] = null;
        }
      });

      newLine.quantity = 1;
      newLine.discount_value = 0;
      newLine.vat_value = 0;
      newLine.total = 0;

      this.localLines.push(newLine);
      this.$emit('input', this.localLines);
    },

    removeLine(index) {
      if (this.localLines.length > 1) {
        let line = this.localLines[index];
        if (line.id) {
          this.$delete(this.lineStocks, line.id);
        }
        this.localLines.splice(index, 1);
        this.$emit('input', this.localLines);
      } else {
        this.$toast.add({
          severity: 'warn',
          summary: 'Aviso',
          detail: 'É necessário pelo menos uma linha',
          life: 3000
        });
      }
    },

    calculateAllLines() {
      this.localLines.forEach((_, index) => {
        this.calculateLine(index);
      });
    },

    calculateLine(index) {
      const line = this.localLines[index];
      const quantity = line.quantity || 0;
      const discountPerc = line.discount || 0;
      const price = line.sale_price !== undefined
      ? line.sale_price
      : 0;

      const subtotal = quantity * price;
      line.discount_value = subtotal * (discountPerc / 100);

      if (this.useVat && Object.keys(line).includes('vat') && line.vat) {
        const vat = this.listVats.find(v => v.code == line.vat);
        const vatPercentage = vat ? vat.value / 100 : 0;
        const subtotalWithDiscount = subtotal - line.discount_value;
        line.vat_value = subtotalWithDiscount * vatPercentage;
        line.total = subtotalWithDiscount * (1 + vatPercentage);
      } else {
        line.total = subtotal - line.discount_value;
      }

      this.$set(this.localLines, index, line);
      this.$emit('input', [...this.localLines]);
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR'
      }).format(value || 0);
    },

    openAddProductDialog(lineData, lineIndex) {
      this.newProductLineIndex = lineIndex;
      this.newProductLineId = lineData.id;
      this.newProduct = {
        ref: typeof lineData.product_ref === 'string' ? lineData.product_ref.trim() : '',
        design: '',
        brand: ''
      };
      this.newProductErrors = {};
      this.showAddProductDialog = true;
    },

    closeAddProductDialog() {
      this.showAddProductDialog = false;
      this.newProductLineIndex = null;
      this.newProductLineId = null;
      this.newProductErrors = {};
    },

    async saveNewProduct() {
      this.newProductErrors = {};

      if (!this.newProduct.ref) {
        this.$set(this.newProductErrors, 'ref', ['A referência é obrigatória']);
      }
      if (!this.newProduct.design) {
        this.$set(this.newProductErrors, 'design', ['A designação é obrigatória']);
      }
      if (!this.newProduct.brand) {
        this.$set(this.newProductErrors, 'brand', ['A marca é obrigatória']);
      }

      if (Object.keys(this.newProductErrors).length > 0) {
        return;
      }

      this.savingProduct = true;
      try {
        const response = await stockService.createProduct({
          ref: this.newProduct.ref,
          design: this.newProduct.design,
          brand: this.newProduct.brand
        });

        const createdProduct = response.data;

        this.$toast.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Produto criado com sucesso.',
          life: 3000
        });

        await this.onProductSelect(createdProduct, this.newProductLineIndex, this.newProductLineId);
        this.closeAddProductDialog();
      } catch (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          this.newProductErrors = err.response.data.errors;
        } else if (err.response && err.response.data && err.response.data.error) {
          this.$toast.add({
            severity: 'error',
            summary: 'Erro',
            detail: err.response.data.error,
            life: 5000
          });
        } else {
          this.$toast.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Erro ao criar produto.',
            life: 5000
          });
        }
      } finally {
        this.savingProduct = false;
      }
    }
  }
};
</script>

<style scoped>
h4 {
  margin-bottom: 1rem;
  color: #495057;
}
:deep(.dossier-lines-table table) {
  table-layout: fixed;
}

.warehouse-option-stock {
  font-size: 0.8rem;
  color: #212529;
  background-color: #ffc107;
  padding: 2px 6px;
  border-radius: 4px;
}

.stocks-info-container {
  background-color: #f8f9fa;
  border-left: 4px solid #17a2b8;
  border-radius: 4px;
  padding: 0.75rem 1rem;
}

.text-info {
  color: #17a2b8;
}

.stocks-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.stock-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.4rem 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stock-ref {
  font-weight: bold;
  font-family: monospace;
  font-size: 0.85rem;
  color: #495057;
  border-right: 1px solid #dee2e6;
  padding-right: 0.5rem;
}

.stock-warehouses-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.stock-badge {
  background-color: #e2f0d9;
  color: #385723;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  font-size: 0.75rem;
  white-space: nowrap;
}
</style>